// Razorpay Checkout helper — server-authoritative pricing.
// Frontend flow:
//   1. Call POST /api/razorpay/create-order with { planKey }
//   2. Backend creates Razorpay order, returns { orderId, keyId, amount, ... }
//   3. We load checkout.js, open the modal with the keyId + orderId
//   4. On handler() success, POST /api/razorpay/verify-payment with signed fields
//   5. Backend verifies signature, calls _activate_plan, returns updated user
//   6. We resolve the promise with the activation result
//
// SECRET IS NEVER TOUCHED — only the public keyId is fetched per order.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHECKOUT_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

let scriptLoadingPromise = null;

function loadCheckoutScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve, reject) => {
    // Reuse if already injected
    const existing = document.querySelector(`script[src="${CHECKOUT_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => reject(new Error("Failed to load payment gateway")));
      return;
    }
    const script = document.createElement("script");
    script.src = CHECKOUT_SCRIPT;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      scriptLoadingPromise = null;
      reject(new Error("Could not load payment gateway script"));
    };
    document.body.appendChild(script);
  });
  return scriptLoadingPromise;
}

function restoreBodyScroll() {
  // Razorpay's modal leaves body.overflow=hidden on dismiss. Force-reset.
  try {
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.querySelectorAll(".razorpay-container, .razorpay-checkout-frame").forEach((el) => el.remove());
  } catch {
    // no-op
  }
}

export async function startRazorpayCheckout({ planKey, user, onSuccess, onError, onDismiss }) {
  let order;
  try {
    const resp = await axios.post(`${API}/razorpay/create-order`, { planKey });
    order = resp.data;
  } catch (err) {
    const message = err?.response?.data?.detail || err?.message || "Could not create payment order";
    if (onError) onError(message);
    throw new Error(message);
  }
  if (!order?.orderId || !order?.keyId) {
    const msg = "Invalid order response from server";
    if (onError) onError(msg);
    throw new Error(msg);
  }

  await loadCheckoutScript();

  return new Promise((resolve, reject) => {
    const options = {
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      order_id: order.orderId,
      name: "MDM Sahayak",
      description: order.planLabel || planKey,
      prefill: {
        name: user?.schoolName || "",
        contact: user?.mobile || "",
      },
      notes: {
        userId: user?.id || "",
        schoolId: user?.schoolId || "",
        planKey,
      },
      theme: { color: "#c2410c" },
      modal: {
        ondismiss: () => {
          restoreBodyScroll();
          if (onDismiss) onDismiss();
          reject(new Error("Payment cancelled"));
        },
      },
      handler: async (response) => {
        try {
          const verify = await axios.post(`${API}/razorpay/verify-payment`, {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            planKey,
          });
          restoreBodyScroll();
          if (onSuccess) onSuccess(verify.data);
          resolve(verify.data);
        } catch (err) {
          restoreBodyScroll();
          const message = err?.response?.data?.detail || "Payment verification failed";
          if (onError) onError(message);
          reject(new Error(message));
        }
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        restoreBodyScroll();
        const reason = resp?.error?.description || "Payment failed";
        if (onError) onError(reason);
        reject(new Error(reason));
      });
      rzp.open();
    } catch (err) {
      restoreBodyScroll();
      const message = err?.message || "Could not open payment window";
      if (onError) onError(message);
      reject(new Error(message));
    }
  });
}
